import React from 'react'
import ReactDOM from 'react-dom'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import styled from 'styled-components'

import { URL_CONST } from 'utils/urlConsts'
import { matchURL } from 'utils/urls'

import { selectDeepfakeAlertTimestamp } from 'containers/Alerts/selectors'

import ActionIcon from 'components/lib/DesignSystem/ActionIcon'
import { Close } from 'components/lib/DesignSystem/Icons'
import Button from 'components/lib/DesignSystem/V3/Button'

import DeepfakeIcon from './DeepfakeIcon'

const ModalContainer = styled.div`
  position: fixed;
  right: 20px;
  bottom: 40px;
  width: 410px;
  height: 260px;
  background: var(--color-elevated);
  border-radius: 12px;
  border: 2px solid var(--warning-color);
  box-shadow: var(--color-elevated-shadow);
  display: flex;
  flex-direction: column;
  z-index: 1000;
`

const CloseButton = styled(ActionIcon)`
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 1;
`

const ModalContent = styled.div`
  text-align: center;
  padding: 30px;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

const Title = styled.h2`
  margin: 0 0 12px 0;
  font-size: 21px;
  font-weight: 600;
  color: var(--color-text);
  line-height: 1.3;
`

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
`

/**
 * React component for displaying deepfake alert modal
 * Note: This modal is automatically hidden on the /deepfake/meetings page
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to call when modal is closed
 * @returns {JSX.Element} React component
 */
const DeepfakeAlertModal = ({ isOpen, onClose }) => {
  const alertTimestamp = useSelector(selectDeepfakeAlertTimestamp)

  const isMeetingsPage = matchURL(URL_CONST.deepfake.meetings)

  const getReviewUrl = () => {
    const urlParams = new URLSearchParams()
    urlParams.set('notification', 'suspicious')
    if (alertTimestamp) {
      urlParams.set('timestamp', alertTimestamp)
    }
    urlParams.set('statusFilter', 'suspicious')
    urlParams.set('liveOnly', 'true')

    return `${URL_CONST.deepfake.meetings}?${urlParams.toString()}`
  }

  const handleClose = () => {
    onClose()
  }

  if (!isOpen || isMeetingsPage) return null

  return ReactDOM.createPortal(
    <ModalContainer>
      <CloseButton id="x-modal_close" Icon={Close} onClick={handleClose} />
      <ModalContent>
        <DeepfakeIcon />

        <Title>Deepfake Suspect Detected</Title>

        <p className="typography-body-1">A potential deepfake was detected in a live meeting</p>

        <ButtonContainer>
          <Link
            to={getReviewUrl()}
            onClick={onClose}
            style={{
              textDecoration: 'none',
              color: 'inherit',
            }}
          >
            <Button id="deepfake-alert-review-meeting" size="small">
              Review meeting
            </Button>
          </Link>
        </ButtonContainer>
      </ModalContent>
    </ModalContainer>,
    document.body
  )
}

export default DeepfakeAlertModal
